"use client";

import * as React from "react";
import Link from "next/link";
import Image from "next/image";
import { Menu, X, ChevronDown, ShoppingBag, ChefHat, Building2, Hospital, Salad, Plane } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@tesserix/web";
import { isComingSoon } from "@/app/(marketing)/products/[slug]/products-data";

// `slug` drives both the link and the launch state — the status itself lives in
// products-data.ts so this menu can't claim a product is shipped when it isn't.
const products = [
  {
    slug: "mark8ly",
    name: "Mark8ly",
    description: "Multi-tenant marketplace platform",
    icon: ShoppingBag,
  },
  {
    slug: "fe3dr",
    name: "Fe3dr",
    description: "Real home-cooked food, delivered",
    icon: ChefHat,
  },
  {
    slug: "helivanta",
    name: "Helivanta",
    description: "Hospital management system",
    icon: Hospital,
  },
  {
    slug: "roamie",
    name: "Roamie",
    description: "Translate, plan and explore abroad",
    icon: Plane,
  },
  {
    slug: "dwellm8",
    name: "Dwellm8",
    description: "Rental management, one record",
    icon: Building2,
  },
  {
    slug: "kora",
    name: "Kora",
    description: "AI-powered nutrition tracking",
    icon: Salad,
  },
].map((p) => ({
  ...p,
  href: `/products/${p.slug}`,
  comingSoon: isComingSoon(p.slug),
}));

const navigation = [
  { name: "About", href: "/about" },
  { name: "Contact", href: "/contact" },
];

export function Navbar() {
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const [productsOpen, setProductsOpen] = React.useState(false);
  const productsRef = React.useRef<HTMLDivElement>(null);
  const mobileMenuRef = React.useRef<HTMLDivElement>(null);

  // Close products dropdown when clicking outside
  React.useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (productsRef.current && !productsRef.current.contains(event.target as Node)) {
        setProductsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Focus trap for mobile menu
  React.useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = "hidden";
      const firstFocusable = mobileMenuRef.current?.querySelector<HTMLElement>(
        'a, button, input, [tabindex]:not([tabindex="-1"])'
      );
      firstFocusable?.focus();
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileMenuOpen]);

  // Close mobile menu on escape
  React.useEffect(() => {
    function handleEscape(event: KeyboardEvent) {
      if (event.key === "Escape") {
        setMobileMenuOpen(false);
        setProductsOpen(false);
      }
    }
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, []);

  return (
    <>
      {/* `backdrop-blur` makes this header a containing block for every
          fixed-position descendant, so a `fixed inset-0` child resolves
          against the header's own ~73px box instead of the viewport. That
          is why the mobile overlay and panel below are siblings of the
          header rather than children of it — nesting them back inside
          silently shrinks the menu to the height of the header. */}
      <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 shadow-sm">
        <nav
          className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 lg:px-8"
          aria-label="Main navigation"
        >
          {/* Logo */}
          <div className="flex lg:flex-1">
            <Link href="/" className="-m-1.5 p-1.5">
              <Image
                src="/logo.png"
                alt="Tesserix"
                width={132}
                height={32}
                sizes="132px"
                priority
              />
            </Link>
          </div>

          {/* Mobile menu button */}
          <div className="flex lg:hidden">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="-m-2.5"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-expanded={mobileMenuOpen}
              aria-controls="mobile-menu"
              aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
            >
              <span className="sr-only">{mobileMenuOpen ? "Close menu" : "Open menu"}</span>
              {mobileMenuOpen ? (
                <X className="h-6 w-6" aria-hidden="true" />
              ) : (
                <Menu className="h-6 w-6" aria-hidden="true" />
              )}
            </Button>
          </div>

          {/* Desktop navigation */}
          <div className="hidden lg:flex lg:gap-x-8 lg:items-center">
            {/* Products Mega Menu */}
            <div ref={productsRef} className="relative">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="text-muted-foreground hover:text-foreground"
                onClick={() => setProductsOpen(!productsOpen)}
                aria-expanded={productsOpen}
                aria-haspopup="true"
              >
                Products
                <ChevronDown
                  className={cn("h-4 w-4 transition-transform", productsOpen && "rotate-180")}
                  aria-hidden="true"
                />
              </Button>

              {/* Mega Menu Dropdown */}
              <div
                className={cn(
                  "absolute left-1/2 -translate-x-1/2 top-full mt-3 w-[500px] rounded-xl border bg-background p-4 shadow-lg transition-all",
                  productsOpen
                    ? "opacity-100 visible translate-y-0"
                    : "opacity-0 invisible -translate-y-2"
                )}
                role="menu"
              >
                <div className="grid grid-cols-2 gap-2">
                  {products.map((product) => (
                    <Link
                      key={product.name}
                      href={product.href}
                      className="flex items-start gap-3 rounded-lg p-3 transition-colors hover:bg-muted"
                      onClick={() => setProductsOpen(false)}
                      role="menuitem"
                    >
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border bg-background">
                        <product.icon className="h-5 w-5 text-foreground" />
                      </div>
                      <div>
                        <span className="flex flex-wrap items-center gap-2">
                          <span className="font-medium text-foreground">{product.name}</span>
                          {product.comingSoon ? (
                            <span className="inline-flex items-center rounded-full border bg-muted/50 px-2 py-0.5 font-mono text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
                              Soon
                            </span>
                          ) : null}
                        </span>
                        <p className="text-sm text-muted-foreground">{product.description}</p>
                      </div>
                    </Link>
                  ))}
                </div>
                <div className="mt-3 pt-3 border-t">
                  <Link
                    href="/products"
                    className="flex items-center justify-center text-sm font-medium text-foreground hover:text-primary"
                    onClick={() => setProductsOpen(false)}
                  >
                    View all products →
                  </Link>
                </div>
              </div>
            </div>

            {navigation.map((item) => (
              <Link
                key={item.name}
                href={item.href}
                className="text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
              >
                {item.name}
              </Link>
            ))}
          </div>

          {/* Balances the logo's lg:flex-1 so the nav links stay centred.
              This held the "Sign in" CTA until the marketing/console split: the
              console is reached at its own URL, so a public marketing site no
              longer advertises a super-admin login. Removing the element
              outright rather than emptying it would leave `justify-between` to
              push the links against the right edge. */}
          <div className="hidden lg:flex lg:flex-1" aria-hidden="true" />
        </nav>
      </header>

      {/* Mobile menu overlay */}
      <div
        className={cn(
          "fixed inset-0 z-40 bg-black/20 backdrop-blur-sm lg:hidden transition-opacity",
          mobileMenuOpen ? "opacity-100 visible" : "opacity-0 invisible"
        )}
        onClick={() => setMobileMenuOpen(false)}
        aria-hidden="true"
      />

      {/* Mobile menu panel */}
      <div
        id="mobile-menu"
        ref={mobileMenuRef}
        className={cn(
          // Closed, the panel is only translated off-screen, so its links stay
          // in the tab order and its role="dialog" stays in the accessibility
          // tree — keyboard and screen-reader users could reach a menu nobody
          // can see. `invisible` takes it out of both. `visibility` is
          // transitioned alongside the transform (it interpolates as a step at
          // the end) so the panel still slides out rather than vanishing.
          // Note: this does not stop the off-screen panel widening the
          // document — that needs `overflow-x: clip` on the root layout.
          "fixed top-0 right-0 z-50 flex h-full w-full max-w-sm flex-col bg-background shadow-xl lg:hidden transition-[transform,visibility] duration-300 ease-in-out",
          mobileMenuOpen
            ? "visible translate-x-0"
            : "invisible translate-x-full"
        )}
        role="dialog"
        aria-modal="true"
        aria-label="Mobile navigation"
      >
        <div className="flex shrink-0 items-center justify-between px-6 py-4 border-b">
          <Image
            src="/logo.png"
            alt="Tesserix"
            width={132}
            height={32}
            sizes="132px"
          />
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="-m-2.5"
            onClick={() => setMobileMenuOpen(false)}
            aria-label="Close menu"
          >
            <X className="h-6 w-6" aria-hidden="true" />
          </Button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-6 space-y-6">
          {/* Products section */}
          <div>
            <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">
              Products
            </h3>
            <div className="space-y-1">
              {products.map((product) => (
                <Link
                  key={product.name}
                  href={product.href}
                  className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-base font-medium text-foreground hover:bg-muted transition-colors"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <product.icon className="h-5 w-5" aria-hidden="true" />
                  {product.name}
                  {product.comingSoon ? (
                    <span className="rounded-full border bg-muted/50 px-2 py-0.5 font-mono text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
                      Soon
                    </span>
                  ) : null}
                </Link>
              ))}
            </div>
          </div>

          {/* Other links */}
          <div>
            <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">
              Company
            </h3>
            <div className="space-y-1">
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  href={item.href}
                  className="block rounded-lg px-3 py-2.5 text-base font-medium text-foreground hover:bg-muted transition-colors"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {item.name}
                </Link>
              ))}
            </div>
          </div>

        </div>
      </div>
    </>
  );
}
